import CircleIcon from '@mui/icons-material/Circle'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import { Box, Button, Skeleton, Tooltip, useTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { format } from 'date-fns'
import { useMsal } from '@azure/msal-react'
import { UpdateStoreHelper } from '../source/StoreHelper'
import { useDispatch, useSelector } from 'react-redux'
import { DayCalendarSkeleton, PickersDay } from '@mui/x-date-pickers'
import { useTranslation } from 'react-i18next'
import axios, { AxiosRequestConfig } from 'axios'
import React, { useCallback, useEffect, useState } from 'react'
import { setLoadingCalendar, setRequireCalendarReload } from '../source/slice/LoadingDateSlice'
import { setProjects } from '../source/slice/ProjectsSlice'
import { ICalendar } from '../interface/ICalendar'
import { ITimecard } from '../interface/ISelectedDate'
import { UpdateStoreHelperJournal } from '../source/StoreHelperJournal'
import { IUser } from '../interface/IUser'
import { IJournal } from '../interface/IJournal'
import { InteractionRequiredAuthError, SilentRequest, AuthenticationResult } from '@azure/msal-browser'
import { getAuthConfig } from '../utils/authApi'
import TodayIcon from '@mui/icons-material/Today'
import ChangedDataWarning, { useUnsavedChanges } from './ChangedDataWarning'
import { DecemberWarning } from './DecemberWarning'
import { setStateCalendarDates } from '@/source/slice/CalendarDatesSlice'
import { IOrdersRef } from '@/interface/IOrder'
import { setOrders } from '@/source/slice/OrderSlice'

type ServerDayProps = {
    day: Date
    outsideCurrentMonth: boolean
    getStatusObj: (other: any, outsideCurrentMonth: any, day: any) => React.ReactElement
    [key: string]: any
}

function ServerDay({ day, outsideCurrentMonth, getStatusObj, ...other }: Readonly<ServerDayProps>) {
    return <Tooltip title={''}>{getStatusObj(other, outsideCurrentMonth, day)}</Tooltip>
}

function renderDay(getStatusObj: ServerDayProps['getStatusObj']) {
    return function DayComponent(props: any) {
        return <ServerDay {...props} getStatusObj={getStatusObj} />;
    };
}

const Calendar = () => {
    const { instance, accounts } = useMsal()
    const { t, i18n } = useTranslation()
    const theme = useTheme()

    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const loadingCalendar: boolean = useSelector((state: any) => state.loadingDate?.loadingCalendar)
    const requireCalendarReload: boolean = useSelector((state: any) => state.loadingDate?.requireCalendarReload)
    const dispatch = useDispatch()

    const { checkUnsaved, resetBaseline } = useUnsavedChanges()
    const [pendingDate, setPendingDate] = useState<Date | null>(null)

    const getDefaultSelectedDate = (): Date => {
        const defaultDate = selectedDate.header?.mserp_timeprofiledate ?? selectedJournal.header?.date

        return defaultDate ? new Date(defaultDate) : new Date()
    }


    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const selectedJournal: IJournal = useSelector((state: any) => state.journal?.value)
    const [selectedDateValue, setSelectedDateValue] = useState<Date>(getDefaultSelectedDate())
    const [selectedMonth, setSelectedMonth] = useState<Date>(new Date(selectedDateValue.getFullYear(), selectedDateValue.getMonth(), 1))
    const [calendarDates, setCalendarDates] = useState<ICalendar[]>([])

    const user: IUser = useSelector((state: any) => state.user?.value)
    const hasTimecard = user?.worker?.mserp_usetimecard;
    const scope : string = process.env.REACT_APP_SCOPE || ''
    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    useEffect(() => {
        const getProjects = (selectedDate: string) => {
            getAuthConfig(instance, accounts[0])
                .then((headers) => {
                    axios
                        .get(`${process.env.REACT_APP_BACKEND_URL}/Projects`, {
                            ...headers,
                            data: {},
                            params: {
                                date: selectedDate,
                            },
                        })
                        .then((response) => {
                            dispatch(setProjects(response.data))
                        })
                        .catch((error) => {
                            console.log(error)
                        })
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        return instance.acquireTokenRedirect(accessTokenRequest)
                    }
                    console.log(error)
                })
        }

        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((auth: AuthenticationResult) => {
                const formattedDate = format(selectedDateValue, 'yyyy-MM-dd')

                // Avoid loading things if user is not yet loaded
                if(!user.worker) {
                    return;
                }

                getProjects(formattedDate)

                if (user.worker.mserp_usetimecard) {
                    UpdateStoreHelper(formattedDate, auth.accessToken)
                } else {
                    UpdateStoreHelperJournal(formattedDate, auth.accessToken)
                }

            })
            .catch((error: Error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
            })
    }, [selectedDateValue, user])

    const getSelectedDate = useCallback(() => {
        dispatch(setLoadingCalendar(true))
        dispatch(setRequireCalendarReload(false))
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/Calendar`, {
                        ...headers,
                        data: {},
                        params: {
                            date: format(selectedMonth, 'yyyy-MM-dd'),
                        },
                    })
                    .then((response) => {
                        setCalendarDates(response.data)
                       dispatch(setStateCalendarDates(response.data))
                    })
                    .catch((error) => { console.log(error) })
                    .finally(() => { dispatch(setLoadingCalendar(false)) })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
                dispatch(setLoadingCalendar(false))
            })
    }, [selectedMonth])

     const getOrders = async () => {
        let newOrders: IOrdersRef[]

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    data: {},
                    params: {
                        date: format(new Date(), 'yyyy-MM-dd'),
                    },
                }
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/Orders`, config)
                    .then((response) => {
                        newOrders = response.data
                        dispatch(setOrders(newOrders))
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                console.log(error)
            })
    }
    
    useEffect(() => {
        getSelectedDate()
    }, [selectedMonth])


    useEffect(() => {
        if (user.worker) {
            if (user?.worker?.mserp_employmentcategory === 'ANÜ' || user?.worker?.mserp_employmentcategory === 'FAK') {
                getOrders()
            }
        }
    }, [selectedDateValue])

    useEffect(() => {
        if (requireCalendarReload) {
            getSelectedDate()
        }
    }, [requireCalendarReload])

    const getStatus = (date: Date) => {
        const matchingDates = calendarDates.filter((d) => isSameCalendarDate(date, new Date(d.date)))

        if (matchingDates.length > 0) {
            return matchingDates[0]
        } else {
            return null
        }
    }

    const isSameCalendarDate = (a: Date, b: Date)=> a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()

    const getStatusObj = (other: any, outsideCurrentMonth: any, day: any) => {
        const isSelected = other.selected;
        const colors = {
            OPEN: {
                bgcolor: theme.palette.action.active,
                bordercolor: theme.palette.success.main,
                color: 'white',
            },
            SAVED: {
                bgcolor: '#ddf0dd',
                bordercolor: '#ddf0dd',
                color: theme.palette.success.main,
            },
            RELEASED: {
                bgcolor: theme.palette.success.main,
                bordercolor: theme.palette.success.main,
                color: 'white',
            },
            ABSENCE: {
                bgcolor: 'rgb(107, 0, 138)',
                bordercolor: 'rgb(107, 0, 138)',
                color: 'white',
            },
            HOLIDAY: {
                bgcolor: '#ECDEF1',
                bordercolor: '#ECDEF1',
                color: 'rgb(107, 0, 138)',
            },
            WEEKEND: {
                bgcolor: theme.palette.action.selected,
                bordercolor: theme.palette.action.selected,
                color: 'white',
            },
            NOTRECORDED: {
                bgcolor: theme.palette.warning.main,
                bordercolor: theme.palette.warning.main,
                color: 'white',
            },
            ERROR: {
                color: theme.palette.error.main,
            },
        }
        const dateInformation = getStatus(day)

        if (dateInformation) {
            const dayStatus = dateInformation?.status
            switch (dayStatus) {
                case 'SAVED':
                    return (
                        <PickersDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.SAVED.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${colors.SAVED.bordercolor}`,
                                color: `${(dateInformation.noCapacity && hasTimecard) ? colors.ERROR.color : colors.SAVED.color} !important`,
                                boxShadow: dateInformation.onCall ? `0 0 0 2px white inset` : undefined,
                            }}
                        />
                    )

                case 'RELEASED':
                    return (
                        <PickersDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.RELEASED.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : `2px solid ${colors.RELEASED.bordercolor}`,
                                color: `${(dateInformation.noCapacity && hasTimecard) ? colors.ERROR.color : colors.RELEASED.color} !important`,
                                boxShadow: dateInformation.onCall ? `0 0 0 2px white inset` : undefined,
                            }}
                        />
                    )

                case 'ABSENCE':
                    return (
                        <PickersDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.ABSENCE.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.ABSENCE.bordercolor,
                                color: `${colors.ABSENCE.color} !important`,
                            }}
                        />
                    )

                case 'HOLIDAY':
                    return (
                        <PickersDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.HOLIDAY.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.HOLIDAY.bordercolor,
                                color: `${colors.HOLIDAY.color} !important`,
                            }}
                        />
                    )

                case 'NOTRECORDED':
                    if (day.getDay() === 0 || day.getDay() === 6)
                        return (
                            <PickersDay
                                {...other}
                                outsideCurrentMonth={outsideCurrentMonth}
                                day={day}
                                sx={{
                                    bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                    borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                                }}
                            />
                        )
                    else
                        return (
                            <PickersDay
                                {...other}
                                outsideCurrentMonth={outsideCurrentMonth}
                                day={day}
                                sx={{
                                    bgcolor: `${colors.NOTRECORDED.bgcolor} !important`,
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                    borderColor: isSelected ? undefined : colors.NOTRECORDED.bordercolor,
                                    color: `${colors.NOTRECORDED.color} !important`,
                                }}
                            />
                        )

                case 'OPEN':
                    if (day.getDay() === 0 || day.getDay() === 6)
                        return (
                            <PickersDay
                                {...other}
                                outsideCurrentMonth={outsideCurrentMonth}
                                day={day}
                                sx={{
                                    bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                    borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                                }}
                            />
                        )
                    else return <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day}
                                            sx={(dateInformation.noCapacity && hasTimecard) ? { color: colors.ERROR.color, border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined } : { border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined }} />

                default:
                    return (
                        <PickersDay
                            {...other}
                            outsideCurrentMonth={outsideCurrentMonth}
                            day={day}
                            sx={{
                                bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                            }}
                        />
                    )
            }
        }
        if (day.getDay() === 0 || day.getDay() === 6)
            return (
                <PickersDay
                    {...other}
                    outsideCurrentMonth={outsideCurrentMonth}
                    day={day}
                    sx={{
                        bgcolor: `${colors.WEEKEND.bgcolor} !important`,
                        border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                        borderColor: isSelected ? undefined : colors.WEEKEND.bordercolor,
                    }}
                />
            )
        else
            return (
                <PickersDay
                    {...other}
                    outsideCurrentMonth={outsideCurrentMonth}
                    day={day}
                    sx={{ border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined }}
                />
            )
    }

    return (
        <>
            <Box sx={{ textAlign: 'center', padding: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                    {selectedDate?.header?.mserp_timeprofiledate || selectedJournal.header?.mserp_journalname ? (
                        (
                            <>
                                <DateCalendar
                                    views={['day', 'month', 'day']}
                                    onChange={(value: any) => {
                                        if (user.worker.mserp_usetimecard) {
                                            const changed = checkUnsaved()
                                            if (changed) setPendingDate(value)
                                            else setSelectedDateValue(value)
                                        } else {
                                            setSelectedDateValue(value)
                                        }
                                    }}
                                    value={selectedDateValue}
                                    disabled={loadingDate || loadingCalendar}
                                    slots={{
                                        day: renderDay(getStatusObj),
                                    }}
                                    showDaysOutsideCurrentMonth
                                    onMonthChange={(month: Date) => setSelectedMonth(month)}
                                    sx={{
                                        width: 280,
                                    }}
                                />
                                <ChangedDataWarning open={pendingDate !== null} onConfirm={() => { if (pendingDate) { setSelectedDateValue(pendingDate); setPendingDate(null); resetBaseline() } }} onCancel={() => setPendingDate(null)} />
                            </>
                        )
                    ) : (
                        <>
                            <Skeleton variant="rounded" sx={{ width: 256, height: 36, m: '12px' }} />
                            <Skeleton variant="rounded" sx={{ width: 256, height: 24, m: '12px' }} />
                            <DayCalendarSkeleton sx={{ width: 280, height: 240, mt: '12px' }} />
                        </>
                    )}
                </LocalizationProvider>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button variant="text" onClick={() => setSelectedDateValue(new Date())} startIcon={<TodayIcon />}
                        disabled={loadingDate || loadingCalendar}>
                    {t('calendar.today')}
                </Button>
            </Box>

            <Box sx={{ textAlign: 'center', padding: 0 }}>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <FiberManualRecordOutlinedIcon color="disabled" sx={{ height: 8 }} />
                    {t('calendar.open')}
                </Box>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon color="primary" sx={{ height: 8 }} />
                    {t('calendar.selected')}
                </Box>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon style={{ color: '#ddf0dd' }} sx={{ height: 8 }} />
                    {t('calendar.saved')}
                </Box>
                <br />
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon color="success" sx={{ height: 8 }} />
                    {t('calendar.released')}
                </Box>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon color="warning" sx={{ height: 8 }} />
                    {t('calendar.notrecorded')}
                </Box>
                <br />
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon style={{ color: '#ECDEF1' }} sx={{ height: 8 }} />
                    {t('calendar.holiday')}
                </Box>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon style={{ color: 'rgb(107, 0, 138)' }} sx={{ height: 8 }} />
                    {t('calendar.absence')}
                </Box>
                <Box component="div" sx={{ display: 'inline', whiteSpace: 'nowrap' }}>
                    <CircleIcon color="disabled" sx={{ height: 8 }} />
                    {t('calendar.weekend')}
                </Box>
            </Box>
            <DecemberWarning notInt={(!user?.worker?.mserp_usetimecard || user?.worker?.mserp_employmentcategory === 'ANÜ')} />
        </>
    )
}

export default Calendar
